/**
 * Место телефона для листа табеля — D214, просьба владельца 25.09.2026:
 * сегодняшний лист заполняют в ресторане, а заполненное не на месте
 * сохраняется с пометкой.
 *
 * Внутри Telegram место даёт `LocationManager` (Bot API 8.0): разрешение
 * спрашивает сам Telegram, один раз. Вне Telegram или в старом клиенте —
 * обычный `navigator.geolocation` браузера. Не дал, не определилось —
 * `null`: решает сервер, запись всё равно проходит.
 *
 * Место держится минуту: лист заполняют подряд десятью строками, и
 * спрашивать GPS на каждую — медленно и ради одного и того же ответа.
 */

import { telegramWebApp } from '@shell/miniapp';

export interface Place {
  lat: number;
  lon: number;
  accuracy_m: number | null;
}

interface LocationData {
  latitude: number;
  longitude: number;
  horizontal_accuracy?: number | null;
}

interface LocationManager {
  isInited: boolean;
  isLocationAvailable: boolean;
  init: (callback?: () => void) => void;
  getLocation: (callback: (data: LocationData | null) => void) => void;
}

const FRESH_MS = 60_000;
const WAIT_MS = 10_000;
let cached: { place: Place; at: number } | null = null;

function fromTelegram(): Promise<Place | null> | null {
  const manager = (telegramWebApp() as unknown as { LocationManager?: LocationManager } | null)
    ?.LocationManager;
  if (!manager) return null;
  return new Promise((resolve) => {
    const ask = () => {
      if (!manager.isLocationAvailable) {
        resolve(null);
        return;
      }
      manager.getLocation((data) =>
        resolve(
          data
            ? {
                lat: data.latitude,
                lon: data.longitude,
                accuracy_m: data.horizontal_accuracy ?? null,
              }
            : null,
        ),
      );
    };
    if (manager.isInited) ask();
    else manager.init(ask);
  });
}

function fromBrowser(): Promise<Place | null> {
  if (typeof navigator === 'undefined' || !navigator.geolocation) return Promise.resolve(null);
  return new Promise((resolve) =>
    navigator.geolocation.getCurrentPosition(
      (position) =>
        resolve({
          lat: position.coords.latitude,
          lon: position.coords.longitude,
          accuracy_m: position.coords.accuracy ?? null,
        }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: WAIT_MS, maximumAge: FRESH_MS },
    ),
  );
}

/** Где сейчас телефон — или `null`, если место не дали или не определилось. */
export async function currentPlace(): Promise<Place | null> {
  if (cached && Date.now() - cached.at < FRESH_MS) return cached.place;
  const timeout = new Promise<null>((resolve) => setTimeout(() => resolve(null), WAIT_MS));
  const place = await Promise.race([fromTelegram() ?? fromBrowser(), timeout]);
  if (place) cached = { place, at: Date.now() };
  return place;
}
